import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type Status = "proposed" | "confirmed" | "synthesising" | "synthesised" | "failed" | "rejected";

const STATUS_FILTERS: { key: "all" | Status; label: string }[] = [
  { key: "all", label: "All" },
  { key: "proposed", label: "Proposed" },
  { key: "synthesised", label: "Synthesised" },
  { key: "failed", label: "Failed" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_STYLES: Record<Status, string> = {
  proposed: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  confirmed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  synthesising: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  synthesised: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  rejected: "bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<Status, string> = {
  proposed: "Needs review",
  confirmed: "Synthesising next",
  synthesising: "Synthesising",
  synthesised: "Ready",
  failed: "Failed",
  rejected: "Rejected",
};

function fmt(d?: string | null) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
}

export default function ResearchCapsules() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<"all" | Status>("all");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["research_capsules_list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_meeting_capsules")
        .select(
          "id, title, organisation, meeting_at, status, auto_merged, confidence, synthesised_at, multimodal_synthesis_run_id, research_meeting_capsule_sources(source_id), run:research_ai_call_runs!research_meeting_capsules_multimodal_synthesis_run_id_fkey(cost_usd)"
        )
        .order("meeting_at", { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) {
        // Fallback in case the implicit FK alias above doesn't resolve in PostgREST
        const { data: d2, error: e2 } = await supabase
          .from("research_meeting_capsules")
          .select(
            "id, title, organisation, meeting_at, status, auto_merged, confidence, synthesised_at, multimodal_synthesis_run_id, research_meeting_capsule_sources(source_id)"
          )
          .order("meeting_at", { ascending: false, nullsFirst: false })
          .limit(500);
        if (e2) throw e2;
        return d2 as any[];
      }
      return data as any[];
    },
  });

  // Realtime
  useEffect(() => {
    const ch = supabase
      .channel("research_capsules_list")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "research_meeting_capsules" },
        () => qc.invalidateQueries({ queryKey: ["research_capsules_list"] })
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  const rows = useMemo(() => {
    const list = (data ?? []) as any[];
    const q = search.trim().toLowerCase();
    return list.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (q && !(`${r.title ?? ""} ${r.organisation ?? ""}`.toLowerCase().includes(q))) return false;
      if (from && r.meeting_at && new Date(r.meeting_at) < new Date(from)) return false;
      if (to && r.meeting_at && new Date(r.meeting_at) > new Date(to + "T23:59:59")) return false;
      return true;
    });
  }, [data, status, search, from, to]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Meeting capsules</h2>
        <p className="text-sm text-muted-foreground">
          Auto-merged from meeting transcripts, slides, agendas and follow-ups every 30 minutes.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={status === f.key ? "default" : "outline"}
            onClick={() => setStatus(f.key)}
          >
            {f.label}
          </Button>
        ))}
        <Input
          placeholder="Search title or organisation…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" />
        <span className="text-muted-foreground text-sm">→</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-10 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 mx-auto animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm">
              No capsules yet. Capsules are auto-merged from your meeting sources every 30 minutes.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-muted-foreground">
                  <th className="text-left p-3 font-medium">Title</th>
                  <th className="text-left p-3 font-medium">Organisation</th>
                  <th className="text-left p-3 font-medium">Meeting</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-center p-3 font-medium">Sources</th>
                  <th className="text-center p-3 font-medium">Auto</th>
                  <th className="text-left p-3 font-medium">Confidence</th>
                  <th className="text-left p-3 font-medium">Synthesised</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const s = (r.status as Status) ?? "proposed";
                  const conf = Number(r.confidence ?? 0);
                  const sourceCount = (r.research_meeting_capsule_sources ?? []).length;
                  return (
                    <tr
                      key={r.id}
                      className={cn("border-b hover:bg-muted/30", idx % 2 === 0 ? "bg-background" : "bg-muted/10")}
                    >
                      <td className="p-3">
                        <Link to={`/partner/research/capsules/${r.id}`} className="font-medium text-primary hover:underline">
                          {r.title}
                        </Link>
                      </td>
                      <td className="p-3">{r.organisation ?? "—"}</td>
                      <td className="p-3 whitespace-nowrap">{fmt(r.meeting_at)}</td>
                      <td className="p-3">
                        <Badge variant="outline" className={cn("border-0", STATUS_STYLES[s])}>
                          {s === "synthesising" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                          {STATUS_LABEL[s]}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">{sourceCount}</td>
                      <td className="p-3 text-center">
                        {r.auto_merged ? <Badge variant="secondary">Auto</Badge> : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 rounded bg-muted overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${Math.min(100, conf * 100)}%` }} />
                          </div>
                          <span className="text-xs font-mono text-muted-foreground">{conf.toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="p-3 whitespace-nowrap text-muted-foreground">{fmt(r.synthesised_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
